/* eslint-disable react-refresh/only-export-components -- router configuration intentionally colocates route wrappers */
import { lazy, Suspense, useEffect } from 'react'
import { createBrowserRouter, Navigate, useRouteError, useLocation } from 'react-router-dom'
import { LoginPage } from '../features/auth/LoginPage'
import { McpConsentPage } from '../features/auth/McpConsentPage'
import { McpConnectionsPage } from '../features/auth/McpConnectionsPage'
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage'
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage'
import { PortalPage } from '../features/auth/PortalPage'
import { UnauthorizedPage } from '../features/auth/UnauthorizedPage'
import { MemberChangeFormPage } from '../features/groups/member-change-form/MemberChangeFormPage'
import { ReleaseEnvironmentBanner } from '../shared/components/ReleaseEnvironmentBanner'
import { useAuth } from '../shared/hooks/useAuth'
import { RoleBasedRoute } from './RoleBasedRoute'
import { GroupsAccessRoute } from '../features/groups/adviser/GroupsAccessRoute'

// Lazy-load portal apps so each loads its own chunk on first visit.
// This reduces the initial bundle from ~3.8MB to the core auth/portal-selector code.
const MedicalApp = lazy(() =>
  import('../features/medical/MedicalApp').then(m => ({ default: m.MedicalApp }))
)
const LifeApp = lazy(() =>
  import('../features/life/LifeApp').then(m => ({ default: m.LifeApp }))
)
const AdviserWorkspace = lazy(() => import('../features/groups/adviser/AdviserWorkspace').then(m => ({ default: m.AdviserWorkspace })))
const GroupsApp = lazy(() =>
  import('../features/groups/GroupsApp').then(m => ({ default: m.GroupsApp }))
)
const PulseApp = lazy(() =>
  import('../features/pulse/PulseApp').then(m => ({ default: m.PulseApp }))
)
const GeneralAdminApp = lazy(() =>
  import('../features/general-admin').then(m => ({ default: m.GeneralAdminApp }))
)
const MarketingApp = lazy(() =>
  import('../features/marketing/MarketingApp').then(m => ({ default: m.MarketingApp }))
)

// Reusable loading fallback for portal chunks (matches existing auth loading style)
function PortalLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-white text-xl">Loading...</div>
    </div>
  )
}

function PortalRouteError() {
  const error = useRouteError()
  const message = error instanceof Error ? error.message : String(error)
  const isStaleChunk = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .* failed/i.test(message)

  useEffect(() => {
    if (!isStaleChunk) return
    const reloadKey = `portal-chunk-reload:${message}`
    if (sessionStorage.getItem(reloadKey)) return
    sessionStorage.setItem(reloadKey, 'attempted')
    window.location.reload()
  }, [isStaleChunk, message])

  return (
    <div className="min-h-screen bg-slate-950 px-6 text-white flex items-center justify-center">
      <div className="max-w-md rounded-xl border border-white/10 bg-white/5 p-8 text-center shadow-xl">
        <h1 className="text-xl font-semibold">
          {isStaleChunk ? 'The portal has just been updated' : 'This page could not be loaded'}
        </h1>
        <p className="mt-3 text-sm text-slate-300">
          {isStaleChunk
            ? 'Refresh to load the latest version. Your saved information will not be affected.'
            : 'Please try again. If the problem continues, contact Employee Lab support.'}
        </p>
        <button type="button" onClick={() => window.location.reload()} className="btn-orange mt-6">
          Refresh page
        </button>
      </div>
    </div>
  )
}

const portalErrorElement = <PortalRouteError />

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/" replace />
  }

  return <><ReleaseEnvironmentBanner groups={pathname.startsWith('/groups')} />{children}</>
}

export const router = createBrowserRouter([
  {path: '/oauth/consent', element: <McpConsentPage />},
  {path: '/oauth/connections', element: <McpConnectionsPage />},
  {
    path: '/',
    element: <LoginPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/change-password',
    element: (
      <ProtectedRoute>
        <ChangePasswordPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/member-changes/:token',
    element: <MemberChangeFormPage />,
  },
  {
    path: '/unauthorized',
    element: (
      <ProtectedRoute>
        <UnauthorizedPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/portal',
    element: (
      <ProtectedRoute>
        <RoleBasedRoute allowedRoles={['employee_lab', 'nzrail_accounts', 'insurer', 'ali']}>
          <PortalPage />
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/adviser',
    errorElement: portalErrorElement,
    element: <ProtectedRoute><RoleBasedRoute allowedRoles={['ali', 'employee_lab']}><Suspense fallback={<PortalLoadingFallback />}><AdviserWorkspace /></Suspense></RoleBasedRoute></ProtectedRoute>,
  },
  {
    path: '/medical/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        {/* insurer role is intentional: they review all Medical claims, without an amount/provider threshold. Internal tabs restrict them to Members + Claims only. */}
        <RoleBasedRoute allowedRoles={['employee_lab', 'insurer']}>
          <Suspense fallback={<PortalLoadingFallback />}>
            <MedicalApp />
          </Suspense>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/life/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        {/* insurer role must NOT access Life — Medical only. nzrail_accounts has no Life tabs either. */}
        <RoleBasedRoute allowedRoles={['employee_lab']}>
          <Suspense fallback={<PortalLoadingFallback />}>
            <LifeApp />
          </Suspense>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/groups/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        <RoleBasedRoute allowedRoles={['employee_lab', 'nzrail_accounts', 'ali']}>
          <GroupsAccessRoute>
            <Suspense fallback={<PortalLoadingFallback />}>
              <GroupsApp />
            </Suspense>
          </GroupsAccessRoute>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/pulse/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        <RoleBasedRoute allowedRoles={['employee_lab', 'nzrail_accounts']}>
          <Suspense fallback={<PortalLoadingFallback />}>
            <PulseApp />
          </Suspense>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/general-admin/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        <RoleBasedRoute allowedRoles={['employee_lab', 'nzrail_accounts']}>
          <Suspense fallback={<PortalLoadingFallback />}>
            <GeneralAdminApp />
          </Suspense>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/marketing/*',
    errorElement: portalErrorElement,
    element: (
      <ProtectedRoute>
        <RoleBasedRoute allowedRoles={['employee_lab']}>
          <Suspense fallback={<PortalLoadingFallback />}>
            <MarketingApp />
          </Suspense>
        </RoleBasedRoute>
      </ProtectedRoute>
    ),
  },
])
